/**
 * useForecastData Hook
 *
 * Custom hook for fetching and managing daily gross progression data.
 * Handles loading current month and historical comparison data.
 *
 * Historical Loading Strategy:
 * - Current month is always loaded/refreshed first
 * - Historical years (previous 3 years) are loaded sequentially with delays
 * - Historical data is cached (isComplete=true) so only loaded once
 * - Progress is shown during multi-year loading
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  getDailyProgression,
  loadDailyGross,
  loadDailyGrossAsync,
  waitForJob,
} from '../services/api';
import { DailyGrossSummary, YearComparisonData, MultiYearAverage, ProjectionTargetData, UnprocessedDealsSummary, DailyGrossSnapshot } from '../types/forecast';
import { ConsolidatedServiceLocationData } from '../types/consolidatedService';
import { ConsolidatedPartsLocationData } from '../types/consolidatedParts';

// Delay between loading historical months (ms) to avoid overwhelming the system
const HISTORICAL_LOAD_DELAY = 1500;

// Number of historical years to load
const HISTORICAL_YEARS_TO_LOAD = 3;

// Load one month at a time to avoid timeouts
const LOAD_BY_MONTH = true;

interface UseForecastDataParams {
  locationId: number;
  year: number;
  month: number;
  /** Selected department for working days calculation */
  department?: string;
}

interface UseForecastDataResult {
  /** Current month daily gross data */
  currentMonthData: DailyGrossSummary | null;
  /** Historical data by year */
  historical: Record<number, DailyGrossSummary | null>;
  /** Working days information (based on NEW_VEHICLE for data lookup consistency) */
  workingDays: {
    total: number;
    elapsed: number;
    remaining: number;
    currentDayNumber: number;
  } | null;
  /** Department-specific working days for display (e.g., SERVICE has fewer days than Sales) */
  displayWorkingDays: {
    total: number;
    elapsed: number;
    remaining: number;
  } | null;
  /** Comparison data at current "days remaining" point */
  comparison: {
    daysRemaining: number;
    byYear: Record<number, YearComparisonData>;
    average: MultiYearAverage;
  } | null;
  /** Projection targets for daily cumulative goals */
  projectionTargets: ProjectionTargetData | null;
  /** Unprocessed deals summary (sold but not yet accounting-processed) */
  unprocessedDeals: UnprocessedDealsSummary | null;
  /** Service gross breakdown by labor pay type */
  serviceGrossByPayType: {
    customer: number;
    warranty: number;
    internal: number;
    other: number;
  } | null;
  /** Service controllable expense accounts */
  serviceControllableExpenses: Array<{
    accountCode: string;
    description: string;
    amount: number;
  }> | null;
  /** Parts controllable expense accounts */
  partsControllableExpenses: Array<{
    accountCode: string;
    description: string;
    amount: number;
  }> | null;
  /** Parts gross breakdown by pay type */
  partsGrossByPayType: {
    customerParts: number;
    warrantyParts: number;
    internalParts: number;
    counterRetail: number;
    counterWholesale: number;
    otherParts: number;
    inventoryAdjustment: number;
  } | null;
  /** Parts sales breakdown by pay type (for GP% calculation) */
  partsSalesByPayType: {
    customerParts: number;
    warrantyParts: number;
    internalParts: number;
    counterRetail: number;
    counterWholesale: number;
    otherParts: number;
  } | null;
  /** Body Shop gross split into Labor vs Parts (locs 1, 12, 44 only — null elsewhere) */
  bodyShopGrossByCategory: {
    labor: number;
    parts: number;
  } | null;
  /** Per-location service data for consolidated view analysis table */
  consolidatedServiceByLocation: ConsolidatedServiceLocationData[] | null;
  /** Per-location parts data for consolidated view analysis table */
  consolidatedPartsByLocation: ConsolidatedPartsLocationData[] | null;
  /** Nightly blue tile snapshots for accurate chart history */
  dailySnapshots: DailyGrossSnapshot[] | null;
  /** Consolidated trending = sum of per-location trends, by department (consolidated view only) */
  consolidatedTrending: {
    byDepartment: {
      NEW_VEHICLE: number;
      USED_VEHICLE: number;
      SERVICE: number;
      PARTS: number;
      BODY_SHOP: number;
      RENTAL: number;
      OTHERS: number;
      ALL: number;
    };
  } | null;
  /** Per-department trending for a single location (each dept uses its own working days) */
  trendingByDepartment: {
    NEW_VEHICLE: number;
    USED_VEHICLE: number;
    SERVICE: number;
    PARTS: number;
    BODY_SHOP: number;
    RENTAL: number;
    OTHERS: number;
    ALL: number;
  } | null;
  /** Available years with data */
  availableYears: number[];
  /** Location name */
  locationName: string;
  /** Loading state for initial fetch */
  loading: boolean;
  /** Loading state for data refresh */
  refreshing: boolean;
  /** Error message if any */
  error: string | null;
  /** Progress message during loading */
  progressMessage: string | null;
  /** Function to refresh current month data and optionally load historical */
  refreshData: (loadHistorical?: boolean) => Promise<void>;
  /** Function to load data (initial load or full refresh) */
  loadData: () => Promise<void>;
}

export function useForecastData({
  locationId,
  year,
  month,
  department,
}: UseForecastDataParams): UseForecastDataResult {
  const [currentMonthData, setCurrentMonthData] = useState<DailyGrossSummary | null>(null);
  const [historical, setHistorical] = useState<Record<number, DailyGrossSummary | null>>({});
  const [workingDays, setWorkingDays] = useState<UseForecastDataResult['workingDays']>(null);
  const [displayWorkingDays, setDisplayWorkingDays] = useState<UseForecastDataResult['displayWorkingDays']>(null);
  const [comparison, setComparison] = useState<UseForecastDataResult['comparison']>(null);
  const [projectionTargets, setProjectionTargets] = useState<ProjectionTargetData | null>(null);
  const [unprocessedDeals, setUnprocessedDeals] = useState<UnprocessedDealsSummary | null>(null);
  const [serviceGrossByPayType, setServiceGrossByPayType] = useState<UseForecastDataResult['serviceGrossByPayType']>(null);
  const [serviceControllableExpenses, setServiceControllableExpenses] = useState<UseForecastDataResult['serviceControllableExpenses']>(null);
  const [partsControllableExpenses, setPartsControllableExpenses] = useState<UseForecastDataResult['partsControllableExpenses']>(null);
  const [partsGrossByPayType, setPartsGrossByPayType] = useState<UseForecastDataResult['partsGrossByPayType']>(null);
  const [partsSalesByPayType, setPartsSalesByPayType] = useState<UseForecastDataResult['partsSalesByPayType']>(null);
  const [bodyShopGrossByCategory, setBodyShopGrossByCategory] = useState<UseForecastDataResult['bodyShopGrossByCategory']>(null);
  const [consolidatedServiceByLocation, setConsolidatedServiceByLocation] = useState<ConsolidatedServiceLocationData[] | null>(null);
  const [consolidatedPartsByLocation, setConsolidatedPartsByLocation] = useState<ConsolidatedPartsLocationData[] | null>(null);
  const [dailySnapshots, setDailySnapshots] = useState<DailyGrossSnapshot[] | null>(null);
  const [consolidatedTrending, setConsolidatedTrending] = useState<UseForecastDataResult['consolidatedTrending']>(null);
  const [trendingByDepartment, setTrendingByDepartment] = useState<UseForecastDataResult['trendingByDepartment']>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [locationName, setLocationName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const loadRequestIdRef = useRef(0);

  // Load data from API
  const loadData = useCallback(async () => {
    // Note: locationId can be 0 (Consolidated view), so check for undefined/null explicitly
    if (locationId === undefined || locationId === null || !year || !month) return;

    const requestId = ++loadRequestIdRef.current;
    const isStale = () => requestId !== loadRequestIdRef.current;

    setLoading(true);
    setError(null);
    // Clear stale data from previous location/department to prevent displaying old values during load
    setCurrentMonthData(null);
    setHistorical({});
    setWorkingDays(null);
    setDisplayWorkingDays(null);
    setComparison(null);
    setProjectionTargets(null);
    setUnprocessedDeals(null);
    setServiceGrossByPayType(null);
    setServiceControllableExpenses(null);
    setPartsControllableExpenses(null);
    setPartsGrossByPayType(null);
    setPartsSalesByPayType(null);
    setBodyShopGrossByCategory(null);
    setConsolidatedServiceByLocation(null);
    setConsolidatedPartsByLocation(null);
    setDailySnapshots(null);
    setConsolidatedTrending(null);
    setTrendingByDepartment(null);

    try {
      // First try to get cached data from DynamoDB
      // Pass department to get correct working days calculation
      const response = await getDailyProgression(locationId, year, month, department);
      if (isStale()) return;

      if (response.success && response.data) {
        const data = response.data;
        setCurrentMonthData(data.currentMonth);
        setHistorical(data.historical);
        setWorkingDays(data.workingDays);
        setDisplayWorkingDays(data.displayWorkingDays ?? null);
        setComparison(data.comparison);
        setProjectionTargets(data.projectionTargets ?? null);
        setUnprocessedDeals(data.unprocessedDeals ?? null);
        setServiceGrossByPayType(data.serviceGrossByPayType ?? null);
        setServiceControllableExpenses(data.serviceControllableExpenses ?? null);
        setPartsControllableExpenses(data.partsControllableExpenses ?? null);
        setPartsGrossByPayType(data.partsGrossByPayType ?? null);
        setPartsSalesByPayType(data.partsSalesByPayType ?? null);
        setBodyShopGrossByCategory(data.bodyShopGrossByCategory ?? null);
        setConsolidatedServiceByLocation(data.consolidatedServiceByLocation ?? null);
        setConsolidatedPartsByLocation(data.consolidatedPartsByLocation ?? null);
        setDailySnapshots(data.dailySnapshots ?? null);
        setConsolidatedTrending(data.consolidatedTrending ?? null);
        setTrendingByDepartment(data.trendingByDepartment ?? null);
        setAvailableYears(data.availableYears);
        setLocationName(data.locationName);
      } else if (response.error) {
        // If no cached data, trigger a load from DealerBuilt
        console.log('No cached data found, loading from DealerBuilt...');
        const loadResponse = await loadDailyGross(locationId, year, month, false);
        if (isStale()) return;

        if (loadResponse.success) {
          // Retry getting the data after load
          const retryResponse = await getDailyProgression(locationId, year, month, department);
          if (isStale()) return;
          if (retryResponse.success && retryResponse.data) {
            const data = retryResponse.data;
            setCurrentMonthData(data.currentMonth);
            setHistorical(data.historical);
            setWorkingDays(data.workingDays);
            setDisplayWorkingDays(data.displayWorkingDays ?? null);
            setComparison(data.comparison);
            setProjectionTargets(data.projectionTargets ?? null);
            setUnprocessedDeals(data.unprocessedDeals ?? null);
                setServiceGrossByPayType(data.serviceGrossByPayType ?? null);
        setServiceControllableExpenses(data.serviceControllableExpenses ?? null);
            setPartsControllableExpenses(data.partsControllableExpenses ?? null);
            setPartsGrossByPayType(data.partsGrossByPayType ?? null);
        setPartsSalesByPayType(data.partsSalesByPayType ?? null);
        setBodyShopGrossByCategory(data.bodyShopGrossByCategory ?? null);
        setConsolidatedServiceByLocation(data.consolidatedServiceByLocation ?? null);
        setConsolidatedPartsByLocation(data.consolidatedPartsByLocation ?? null);
        setDailySnapshots(data.dailySnapshots ?? null);
        setConsolidatedTrending(data.consolidatedTrending ?? null);
        setTrendingByDepartment(data.trendingByDepartment ?? null);
            setAvailableYears(data.availableYears);
            setLocationName(data.locationName);
          } else {
            setError(retryResponse.error || 'Failed to load data');
          }
        } else {
          setError(loadResponse.error || 'Failed to load data from DealerBuilt');
        }
      }
    } catch (err: any) {
      if (isStale()) return;
      console.error('Error loading forecast data:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [locationId, year, month, department]);

  // Helper to delay execution
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Refresh current month data and optionally load historical years
  const refreshData = useCallback(async (loadHistorical: boolean = false) => {
    // Note: locationId can be 0 (Consolidated view), so check for undefined/null explicitly
    if (locationId === undefined || locationId === null || !year || !month) return;

    setRefreshing(true);
    setError(null);
    setProgressMessage('Loading current month data...');

    try {
      // Step 1: Force refresh current month from DealerBuilt (also trigger snapshot writer for CEO briefing)
      const loadResponse = await loadDailyGross(locationId, year, month, true, true);

      if (!loadResponse.success) {
        setError(loadResponse.error || 'Failed to refresh data from DealerBuilt');
        setProgressMessage(null);
        setRefreshing(false);
        return;
      }

      // Get updated data for current month
      // Pass department to get correct working days calculation
      let response = await getDailyProgression(locationId, year, month, department);
      if (response.success && response.data) {
        const data = response.data;
        setCurrentMonthData(data.currentMonth);
        setHistorical(data.historical);
        setWorkingDays(data.workingDays);
        setDisplayWorkingDays(data.displayWorkingDays ?? null);
        setComparison(data.comparison);
        setProjectionTargets(data.projectionTargets ?? null);
        setUnprocessedDeals(data.unprocessedDeals ?? null);
        setServiceGrossByPayType(data.serviceGrossByPayType ?? null);
        setServiceControllableExpenses(data.serviceControllableExpenses ?? null);
        setPartsControllableExpenses(data.partsControllableExpenses ?? null);
        setPartsGrossByPayType(data.partsGrossByPayType ?? null);
        setPartsSalesByPayType(data.partsSalesByPayType ?? null);
        setBodyShopGrossByCategory(data.bodyShopGrossByCategory ?? null);
        setConsolidatedServiceByLocation(data.consolidatedServiceByLocation ?? null);
        setConsolidatedPartsByLocation(data.consolidatedPartsByLocation ?? null);
        setDailySnapshots(data.dailySnapshots ?? null);
        setConsolidatedTrending(data.consolidatedTrending ?? null);
        setTrendingByDepartment(data.trendingByDepartment ?? null);
        setAvailableYears(data.availableYears);
        setLocationName(data.locationName);
      }

      // Step 2: Load historical data if requested (using async job system)
      // Load month by month to avoid API Gateway timeouts
      if (loadHistorical) {
        // Build list of months to load (same month for each historical year)
        const monthsToLoad: Array<{ year: number; month: number; label: string }> = [];

        for (let i = 1; i <= HISTORICAL_YEARS_TO_LOAD; i++) {
          const historicalYear = year - i;

          // Check if we already have complete data for this year
          const existingData = historical[historicalYear];
          if (existingData?.isComplete) {
            console.log(`Historical year ${historicalYear} already complete, skipping...`);
            continue;
          }

          monthsToLoad.push({
            year: historicalYear,
            month: month,
            label: `${historicalYear}-${month.toString().padStart(2, '0')}`
          });
        }

        const totalMonths = monthsToLoad.length;
        console.log(`Loading ${totalMonths} historical months...`);

        for (let i = 0; i < monthsToLoad.length; i++) {
          const { year: histYear, month: histMonth, label } = monthsToLoad[i];

          setProgressMessage(`Loading ${label} (${i + 1}/${totalMonths})...`);

          // Add delay between requests to avoid overwhelming the system
          if (i > 0) {
            await delay(HISTORICAL_LOAD_DELAY);
          }

          try {
            // Trigger async job for this specific month
            const triggerResponse = await loadDailyGrossAsync(locationId, histYear, histMonth, false);

            if (!triggerResponse.success || !triggerResponse.data) {
              console.warn(`Failed to trigger ${label} job:`, triggerResponse.error);
              continue;
            }

            const { jobId } = triggerResponse.data;
            console.log(`Started async job ${jobId} for ${label}`);

            // Wait for job to complete with progress updates
            const jobResult = await waitForJob(
              jobId,
              (progress, message) => {
                setProgressMessage(`Loading ${label} (${i + 1}/${totalMonths}): ${message} (${progress}%)`);
              },
              2000, // Poll every 2 seconds
              180000 // 3 minute timeout per month (should be plenty)
            );

            if (jobResult.success && jobResult.data?.status === 'COMPLETED') {
              console.log(`✓ Successfully loaded ${label}`);

              // Refresh the progression data to include the newly loaded historical data
              const updatedResponse = await getDailyProgression(locationId, year, month, department);
              if (updatedResponse.success && updatedResponse.data) {
                const data = updatedResponse.data;
                setHistorical(data.historical);
                setComparison(data.comparison);
                setAvailableYears(data.availableYears);
              }
            } else {
              const errorMsg = jobResult.data?.error || jobResult.error || 'Unknown error';
              console.warn(`✗ Failed to load ${label}:`, errorMsg);
            }
          } catch (histErr) {
            console.warn(`Error loading ${label}:`, histErr);
            // Continue with next month even if one fails
          }
        }
      }

      setProgressMessage(null);
    } catch (err: any) {
      console.error('Error refreshing forecast data:', err);
      setError(err.message || 'An unexpected error occurred');
      setProgressMessage(null);
    } finally {
      setRefreshing(false);
    }
  }, [locationId, year, month, department, historical]);

  // Auto-load on mount and when params change
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    currentMonthData,
    historical,
    workingDays,
    displayWorkingDays,
    comparison,
    projectionTargets,
    unprocessedDeals,
    serviceGrossByPayType,
    serviceControllableExpenses,
    partsControllableExpenses,
    partsGrossByPayType,
    partsSalesByPayType,
    bodyShopGrossByCategory,
    consolidatedServiceByLocation,
    consolidatedPartsByLocation,
    dailySnapshots,
    consolidatedTrending,
    trendingByDepartment,
    availableYears,
    locationName,
    loading,
    refreshing,
    error,
    progressMessage,
    refreshData,
    loadData,
  };
}
